import React, { useRef, useState } from "react";
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  Paper,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { BRAND_BLUE, BRAND_GREEN } from "../../theme/colors";
import { PRIMARY_BUTTON_SX } from "../../theme/buttonstyles";
import CountryDropdown from "../../shared/forms/CountriesDropDown";
import StateDropdown from "../../shared/forms/StateProvinceDropDown";
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

export const EMPTY_PROPERTY = {
  name: "",
  propertyType: "",
  status: "Occupied",
  yearBuilt: "",
  numberOfFloors: "",
  lotSizeAcres: "",
  buildingDescription: "",
  utilitiesAvailable: "",
  image: "",
  images: [],
  streetAddress: "",
  city: "",
  state: "",
  zipCode: "",
  country: "United States",
  latitude: "",
  longitude: "",
  squareFeet: "",
};

const PROPERTY_TYPES = [
  "Office",
  "Industrial",
  "Retail",
  "Residential",
  "Warehouse",
  "Mixed use",
  "Land",
  "Hospitality",
  "Multifamily",
  "Other",
];
const PROPERTY_STATUSES = ["Occupied", "Vacant", "Under Construction"];
const DROPDOWN_FIELD_SX = {
  "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused": {
    color: BRAND_BLUE,
  },
  "& .MuiSelect-select, & .MuiSelect-icon": {
    color: BRAND_BLUE,
  },
};
const DROPDOWN_MENU_PROPS = {
  PaperProps: {
    sx: {
      "& .MuiMenuItem-root": {
        color: BRAND_BLUE,
      },
    },
  },
};

function readImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("The selected image could not be read."));
    reader.readAsDataURL(file);
  });
}

export default function PropertyForm({
  initialValue = EMPTY_PROPERTY,
  title,
  description,
  submitLabel,
  saving = false,
  onSubmit,
  onCancel,
}) {
  const [form, setForm] = useState(() => {
    const value = { ...EMPTY_PROPERTY, ...initialValue };
    const images = value.images?.length ? value.images : value.image ? [value.image] : [];
    return { ...value, image: images[0] || "", images };
  });
  const [imageError, setImageError] = useState("");
  const fileInputRef = useRef(null);

  const handleChange = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleCountryChange = (event) => {
    const nextCountry = event.target.value;
    setForm((current) => ({
      ...current,
      country: nextCountry,
      state: nextCountry === current.country ? current.state : "",
    }));
  };

  const handleImage = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    if (files.some((file) => !file.type.startsWith("image/"))) {
      setImageError("Every selected file must be a PNG, JPG, or WebP image.");
      return;
    }
    if (files.some((file) => file.size > MAX_IMAGE_BYTES)) {
      setImageError("Each image must be smaller than 5 MB.");
      return;
    }
    try {
      const images = await Promise.all(files.map(readImage));
      setForm((current) => {
        const nextImages = [...current.images, ...images];
        return { ...current, image: nextImages[0] || "", images: nextImages };
      });
      setImageError("");
    } catch (error) {
      setImageError(error.message);
    }
  };

  return (
    <Paper
      component="form"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(form);
      }}
      elevation={0}
      sx={{
        overflow: "hidden",
        borderRadius: 2,
        border: "1px solid #dfe5ec",
        boxShadow: "0 4px 14px rgba(15,23,42,.055)",
        bgcolor: "white",
      }}
    >
      <Box sx={{ p: { xs: 2, md: 2.5 }, borderBottom: `1px solid ${alpha(BRAND_BLUE, 0.09)}` }}>
        <Typography variant="overline" sx={{ color: BRAND_GREEN, fontWeight: 800, letterSpacing: 1.2 }}>Property management</Typography>
        <Typography variant="h6" sx={{ color: BRAND_BLUE, fontWeight: 800, lineHeight: 1.25 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {description}
        </Typography>
      </Box>

      <Box
        sx={{
          p: { xs: 2, md: 2.5 },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(240px, .7fr) minmax(0, 1.3fr)" },
          gap: { xs: 2.5, md: 3 },
        }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, color: BRAND_BLUE, fontWeight: 700 }}>
            Property image gallery
          </Typography>
          <Box
            role="button"
            tabIndex={0}
            aria-label="Upload property photos"
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") fileInputRef.current?.click();
            }}
            sx={{
              minHeight: 260,
              borderRadius: 2,
              border: `1.5px dashed ${imageError ? "#d32f2f" : alpha(BRAND_BLUE, 0.28)}`,
              bgcolor: alpha(BRAND_BLUE, 0.025),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              "&:hover": { borderColor: BRAND_GREEN, bgcolor: alpha(BRAND_GREEN, 0.04) },
              "&:focus-visible": { outline: `3px solid ${alpha(BRAND_GREEN, 0.35)}` },
            }}
          >
            {(
              <Box sx={{ p: 3, textAlign: "center" }}>
                <CloudUploadOutlinedIcon sx={{ fontSize: 42, color: BRAND_GREEN, mb: 1 }} />
                <Typography sx={{ color: BRAND_BLUE, fontWeight: 700 }}>Upload property photos</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  PNG, JPG, or WebP · up to 5 MB
                </Typography>
              </Box>
            )}
          </Box>
          <input ref={fileInputRef} hidden multiple type="file" accept="image/png,image/jpeg,image/webp" onChange={handleImage} />
          {imageError && <Typography variant="caption" color="error" sx={{ mt: 0.75, display: "block" }}>{imageError}</Typography>}
          {form.images.length > 0 && (
            <Box sx={{ mt: 2, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.25 }}>
              {form.images.map((image, index) => (
                <Box key={`${image.slice(0, 24)}-${index}`} sx={{ overflow: "hidden", borderRadius: 2, border: `2px solid ${index === 0 ? BRAND_GREEN : alpha(BRAND_BLUE, 0.12)}` }}>
                  <Box component="img" src={image} alt={`Property upload ${index + 1}`} sx={{ display: "block", width: "100%", height: 100, objectFit: "cover" }} />
                  <Box sx={{ p: 0.5, display: "flex", justifyContent: "space-between", gap: 0.5 }}>
                    <Button
                      size="small"
                      disabled={index === 0}
                      onClick={() => setForm((current) => {
                        const images = [image, ...current.images.filter((_, itemIndex) => itemIndex !== index)];
                        return { ...current, image: images[0], images };
                      })}
                      sx={{ minWidth: 0, px: 0.5, fontSize: 11, textTransform: "none" }}
                    >
                      {index === 0 ? "Primary" : "Set primary"}
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => setForm((current) => {
                        const images = current.images.filter((_, itemIndex) => itemIndex !== index);
                        return { ...current, image: images[0] || "", images };
                      })}
                      sx={{ minWidth: 0, px: 0.5, fontSize: 11, textTransform: "none" }}
                    >
                      Delete
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 2, alignContent: "start", "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}>
          <TextField required label="Property name" value={form.name} onChange={handleChange("name")} inputProps={{ maxLength: 255 }} fullWidth sx={{ gridColumn: "1 / -1" }} />
          <TextField
            required
            select
            label="Property type"
            value={form.propertyType}
            onChange={handleChange("propertyType")}
            fullWidth
            SelectProps={{ MenuProps: DROPDOWN_MENU_PROPS }}
            sx={DROPDOWN_FIELD_SX}
          >
            {PROPERTY_TYPES.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
          </TextField>
          <TextField
            required
            select
            label="Current status"
            value={form.status}
            onChange={handleChange("status")}
            fullWidth
            SelectProps={{ MenuProps: DROPDOWN_MENU_PROPS }}
            sx={DROPDOWN_FIELD_SX}
          >
            {PROPERTY_STATUSES.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
          </TextField>
          <Box sx={{ gridColumn: "1 / -1" }}>
            <TextField
              required
              label="Street address"
              value={form.streetAddress}
              onChange={handleChange("streetAddress")}
              placeholder="e.g. 4600 Silver Hill Rd, Washington, DC 20233"
              inputProps={{ maxLength: 255 }}
              fullWidth
            />
          </Box>
          <TextField required label="City" value={form.city} onChange={handleChange("city")} inputProps={{ maxLength: 100 }} fullWidth />
          <CountryDropdown required label="Country" value={form.country} onChange={handleCountryChange} sx={DROPDOWN_FIELD_SX} />
          <StateDropdown required label="State / Province" country={form.country} value={form.state} onChange={handleChange("state")} sx={DROPDOWN_FIELD_SX} />
          <TextField required label="ZIP / Postal Code" value={form.zipCode} onChange={handleChange("zipCode")} inputProps={{ maxLength: 20 }} fullWidth />
          <TextField required label="Square footage" type="number" value={form.squareFeet} onChange={handleChange("squareFeet")} inputProps={{ min: 0 }} fullWidth />
          <TextField label="Year built" type="number" value={form.yearBuilt} onChange={handleChange("yearBuilt")} inputProps={{ min: 1000, max: 3000 }} fullWidth />
          <TextField label="Number of floors" type="number" value={form.numberOfFloors} onChange={handleChange("numberOfFloors")} inputProps={{ min: 0, max: 1000 }} fullWidth />
          <TextField label="Lot size (acres)" type="number" value={form.lotSizeAcres} onChange={handleChange("lotSizeAcres")} inputProps={{ min: 0, step: "any" }} fullWidth />
          <TextField
            label="Building description"
            value={form.buildingDescription}
            onChange={handleChange("buildingDescription")}
            multiline
            minRows={4}
            fullWidth
            sx={{ gridColumn: "1 / -1" }}
          />
          <TextField
            label="Available utilities & infrastructure"
            value={form.utilitiesAvailable}
            onChange={handleChange("utilitiesAvailable")}
            multiline
            minRows={3}
            placeholder="Describe available electric, gas, water, sewer, broadband, or other utility services"
            fullWidth
            sx={{ gridColumn: "1 / -1" }}
          />
        </Box>
      </Box>

      <Box sx={{ px: { xs: 2, md: 2.5 }, py: 1.75, display: "flex", justifyContent: "flex-end", gap: 1.5, borderTop: `1px solid ${alpha(BRAND_BLUE, 0.09)}`, bgcolor: "#f7f9fc" }}>
        <Button type="button" disabled={saving} onClick={onCancel} sx={{ textTransform: "none", fontWeight: 700, color: "text.secondary" }}>Cancel</Button>
        <Button type="submit" disabled={saving} variant="contained" sx={{ ...PRIMARY_BUTTON_SX, minWidth: 130, px: 3 }}>
          {saving ? <CircularProgress size={22} color="inherit" /> : submitLabel}
        </Button>
      </Box>
    </Paper>
  );
}
